import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { AdminLayout } from "../../components/admin/AdminLayout";
import {
  useSiteSettings,
  useUpdateSiteSetting,
  type SiteSetting,
} from "../../hooks/useSiteSettings";

export const SiteSettingsPage: React.FC = () => {
  const { data: settings, isLoading } = useSiteSettings();
  const updateMutation = useUpdateSiteSetting();

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSetting, setEditingSetting] = useState<SiteSetting | null>(null);

  const shouldUseModal = (setting: SiteSetting): boolean => {
    // Use modal for text fields that are multiline or longer than 50 characters
    return (
      setting.value_type === "text" ||
      setting.value.includes("\n") ||
      setting.value.length > 50
    );
  };

  const handleEditStart = (setting: SiteSetting) => {
    if (shouldUseModal(setting)) {
      setEditingSetting(setting);
      setEditValue(setting.value);
      setModalOpen(true);
    } else {
      setEditingKey(setting.key);
      setEditValue(setting.value);
    }
  };

  const handleEditCancel = () => {
    setEditingKey(null);
    setEditValue("");
    setModalOpen(false);
    setEditingSetting(null);
  };

  const handleEditSave = async (key: string) => {
    await updateMutation.mutateAsync({
      key,
      data: {
        value: editValue,
      },
    });
    setEditingKey(null);
    setEditValue("");
    setModalOpen(false);
    setEditingSetting(null);
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            mb: 3,
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              color: "text.primary",
            }}
          >
            Site Settings
          </Typography>
        </Box>

        {settings?.settings && settings.settings.length > 0 ? (
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
            }}
          >
            {settings.settings
              .filter(
                (setting) =>
                  !setting.key.includes("_image_url") &&
                  !setting.key.includes("_image") &&
                  !setting.key.includes("font") &&
                  !setting.key.includes("description") &&
                  setting.key !== "site_description" &&
                  setting.key !== "about_text"
              )
              .map((setting, index, filteredArray) => (
                <Box
                  key={setting.key}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    py: 2,
                    borderBottom:
                      index < filteredArray.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "0.875rem", sm: "0.9rem" },
                      color: "text.primary",
                    }}
                  >
                    {setting.description || setting.key}
                  </Typography>

                  {editingKey === setting.key ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <TextField
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={10}
                        size="small"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && e.ctrlKey) {
                            handleEditSave(setting.key);
                          } else if (e.key === "Escape") {
                            handleEditCancel();
                          }
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: { xs: "0.875rem", sm: "0.9rem" },
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "flex-end",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={handleEditCancel}
                          disabled={updateMutation.isPending}
                          sx={{
                            color: "text.secondary",
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditSave(setting.key)}
                          disabled={updateMutation.isPending}
                          sx={{
                            color: "primary.main",
                            "&:hover": {
                              backgroundColor: "primary.light",
                              color: "primary.dark",
                            },
                          }}
                        >
                          {updateMutation.isPending ? (
                            <CircularProgress size={16} />
                          ) : (
                            <SaveIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          wordBreak: "break-word",
                          color: "text.primary",
                          fontSize: { xs: "0.875rem", sm: "0.9rem" },
                          lineHeight: 1.5,
                        }}
                      >
                        {setting.value}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleEditStart(setting)}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "primary.main",
                            backgroundColor: "action.hover",
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}
          </Box>
        ) : (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No settings found.
            </Typography>
          </Box>
        )}

        {/* Modal for multiline/long text fields */}
        <Dialog
          open={modalOpen}
          onClose={handleEditCancel}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            {editingSetting?.description || editingSetting?.key}
          </DialogTitle>
          <DialogContent>
            <TextField
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              fullWidth
              multiline
              minRows={15}
              maxRows={25}
              autoFocus
              sx={{
                mt: 1,
                "& .MuiOutlinedInput-root": {
                  fontSize: { xs: "0.875rem", sm: "0.9rem" },
                },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
            <Button onClick={handleEditCancel} size="small">
              Cancel
            </Button>
            <Button
              onClick={() => editingSetting && handleEditSave(editingSetting.key)}
              variant="contained"
              size="small"
              disabled={updateMutation.isPending || !editValue}
            >
              {updateMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                "Save"
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
